"use client";

import { useStrictLocalization } from "@faire/web/common/localization";
import { legacyComponentCls } from "@faire/web/slate/utils/tracking";
import * as React from "react";

import { Column, HorizontalLine } from "../Layout";
import { Spacer } from "../spacing";
import { Typography } from "../Typography";
import { Align } from "../Typography/Types";

import { AccordionItem, AccordionListItem } from "./AccordionItem";

export type AccordionProps = {
  /** Each Item contains a title (question) and a body (answer) which are react nodes, but will default to Typography if you pass in a string */
  items: AccordionItem[];
  /** Callback function that triggers when an faqItem is clicked */
  onItemClick?: (index: number) => void;
  /** The initially expanded question */
  initiallyOpenIndex?: number;
  /** The title displayed at the top of the Accordion */
  title?: string;
  /** How to align the title, default to "center" **/
  titleAlignment?: Align;
  /** If enabled, we will use serif font - matches the rest of brand portal */
  useSerif?: boolean;
  /** The test id */
  "data-test-id"?: string;
};

/**
 * @deprecated use AccordionGroup from `@faire/slate/components/accordion` since 2025-06-20
 *
 * @trackcomponent
 */
export const Accordion: React.FC<AccordionProps> = ({
  items,
  onItemClick,
  initiallyOpenIndex = -1,
  title: _title,
  titleAlignment = "center",
  "data-test-id": testId,
  useSerif,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const title =
    _title ??
    strictLocalize({
      id: "wzhLtV",
      defaultMessage: "You may be wondering",
      description: { text: "Frequently asked questions title" },
    });
  const [openIndex, setOpenIndex] = React.useState(initiallyOpenIndex);

  const handleClick = (index: number) => {
    if (index === openIndex) {
      setOpenIndex(-1);
    } else {
      setOpenIndex(index);
    }
    onItemClick?.(index);
  };

  return (
    <Column className={legacyComponentCls()}>
      <Typography
        variant={useSerif ? "sectionHeaderSansMedium" : "displaySSerifRegular"}
        align={titleAlignment}
        data-test-id={testId && `${testId}-title`}
      >
        {title}
      </Typography>
      <Spacer height="3x" />
      {items.map((faqItem, index) => (
        <React.Fragment key={index}>
          {index === 0 ? <HorizontalLine /> : null}
          <AccordionListItem
            {...faqItem}
            onClick={() => handleClick(index)}
            isCollapsed={openIndex !== index}
            data-test-id={testId && `${testId}__item-${index}`}
          />
          <HorizontalLine />
        </React.Fragment>
      ))}
    </Column>
  );
};
Accordion.displayName = "Accordion";
